import React, { useState, useEffect } from 'react';
import { Eye, Users, TrendingUp, Globe, Mail } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../lib/api';

const MiniStat = ({ icon: Icon, label, value, testId }) => (
  <Card className="bg-[#f1e3eb]/60 border border-[#bd8cae]/20">
    <CardContent className="p-5 text-center">
      <div className="w-10 h-10 rounded-full bg-[#5d6b3c]/15 flex items-center justify-center mx-auto mb-2">
        <Icon className="w-5 h-5 text-[#5d6b3c]" />
      </div>
      <p className="font-serif text-2xl text-[#3c4a2a]" data-testid={testId}>{value}</p>
      <p className="text-xs text-[#3c4a2a]/60 mt-1">{label}</p>
    </CardContent>
  </Card>
);

const DailyChart = ({ daily }) => {
  const max = Math.max(1, ...daily.map((d) => d.views));
  return (
    <div className="flex items-end gap-1 h-40" data-testid="traffic-daily-chart">
      {daily.map((d) => (
        <div key={d.day} className="flex-1 h-full flex flex-col items-center justify-end group relative">
          <div className="absolute -top-8 hidden group-hover:block bg-[#3c4a2a] text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
            {d.day}: {d.views} views · {d.visitors} visitors
          </div>
          <div
            className="w-full bg-[#5d6b3c] rounded-t hover:bg-[#3c4a2a] transition-colors"
            style={{ height: `${Math.max(2, (d.views / max) * 100)}%` }}
          />
        </div>
      ))}
    </div>
  );
};

const TopList = ({ title, rows, labelKey, testId }) => (
  <div data-testid={testId}>
    <h4 className="font-serif text-lg text-[#3c4a2a] mb-3">{title}</h4>
    {rows.length === 0 ? (
      <p className="text-sm text-[#3c4a2a]/50">No data yet</p>
    ) : (
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r[labelKey]} className="flex items-center justify-between text-sm bg-[#f1e3eb]/50 rounded-lg px-4 py-2">
            <span className="text-[#3c4a2a] truncate mr-3">{r[labelKey]}</span>
            <span className="font-medium text-[#5d6b3c] flex-shrink-0">{r.views}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export const AnalyticsAdmin = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get('/admin/analytics').then((res) => setData(res.data)).catch(() => setError('Could not load analytics'));
  }, []);

  const sendReport = async () => {
    setSending(true);
    try {
      const res = await api.post('/admin/analytics/send-report');
      toast.success(res.data.message);
    } catch {
      toast.error('Could not send the report — please try again.');
    } finally {
      setSending(false);
    }
  };

  if (error) return <p className="text-sm text-red-600" data-testid="traffic-error">{error}</p>;
  if (!data) return <p className="text-sm text-[#3c4a2a]/50">Loading traffic data…</p>;

  return (
    <div className="space-y-8" data-testid="traffic-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h3 className="font-serif text-2xl text-[#3c4a2a]">Site Traffic</h3>
          <p className="text-sm text-[#3c4a2a]/60 mt-1">
            Visitors are anonymous — counts are based on browsers, not names. A summary email goes to board admins every Monday morning.
          </p>
        </div>
        <Button
          onClick={sendReport}
          disabled={sending}
          data-testid="traffic-send-report-btn"
          variant="outline"
          className="border-[#5d6b3c] text-[#5d6b3c] hover:bg-[#5d6b3c] hover:text-white flex-shrink-0"
        >
          <Mail className="w-4 h-4 mr-2" />
          {sending ? 'Sending…' : 'Email Me This Report'}
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MiniStat icon={Eye} label="Views Today" value={data.views_today} testId="traffic-views-today" />
        <MiniStat icon={Users} label="Visitors Today" value={data.visitors_today} testId="traffic-visitors-today" />
        <MiniStat icon={TrendingUp} label="Views (30 days)" value={data.total_views} testId="traffic-views-30d" />
        <MiniStat icon={Globe} label="Unique Visitors (30 days)" value={data.unique_visitors} testId="traffic-visitors-30d" />
      </div>

      <div>
        <h4 className="font-serif text-lg text-[#3c4a2a] mb-3">Daily Views — Last 30 Days</h4>
        <DailyChart daily={data.daily} />
        <div className="flex justify-between text-xs text-[#3c4a2a]/50 mt-2">
          <span>{data.daily[0]?.day}</span>
          <span>{data.daily[data.daily.length - 1]?.day}</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <TopList title="Most Viewed Pages" rows={data.top_pages} labelKey="path" testId="traffic-top-pages" />
        <TopList title="Traffic Sources" rows={data.top_referrers} labelKey="referrer" testId="traffic-top-referrers" />
      </div>
    </div>
  );
};
